/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { Operation } from './Operation';
import {
    OperationFromJSON,
    OperationFromJSONTyped,
    OperationToJSON,
    OperationToJSONTyped,
} from './Operation';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { QuotationRequestServiceOffer } from './QuotationRequestServiceOffer';
import {
    QuotationRequestServiceOfferFromJSON,
    QuotationRequestServiceOfferFromJSONTyped,
    QuotationRequestServiceOfferToJSON,
    QuotationRequestServiceOfferToJSONTyped,
} from './QuotationRequestServiceOffer';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';
import type { Rejection } from './Rejection';
import {
    RejectionFromJSON,
    RejectionFromJSONTyped,
    RejectionToJSON,
    RejectionToJSONTyped,
} from './Rejection';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { Party } from './Party';
import {
    PartyFromJSON,
    PartyFromJSONTyped,
    PartyToJSON,
    PartyToJSONTyped,
} from './Party';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { QuotationBatchOfGoods } from './QuotationBatchOfGoods';
import {
    QuotationBatchOfGoodsFromJSON,
    QuotationBatchOfGoodsFromJSONTyped,
    QuotationBatchOfGoodsToJSON,
    QuotationBatchOfGoodsToJSONTyped,
} from './QuotationBatchOfGoods';
import type { FinancialSummary } from './FinancialSummary';
import {
    FinancialSummaryFromJSON,
    FinancialSummaryFromJSONTyped,
    FinancialSummaryToJSON,
    FinancialSummaryToJSONTyped,
} from './FinancialSummary';
import type { PlannedScheduledLocation } from './PlannedScheduledLocation';
import {
    PlannedScheduledLocationFromJSON,
    PlannedScheduledLocationFromJSONTyped,
    PlannedScheduledLocationToJSON,
    PlannedScheduledLocationToJSONTyped,
} from './PlannedScheduledLocation';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';

/**
 * Response schema for Quotation logistics object.
 * @export
 * @interface QuotationResponse
 */
export interface QuotationResponse {
    /**
     * Name given to the quotation by the requestor (SELF_CARE process) or the provider (CRM process).
     * @type {string}
     * @memberof QuotationResponse
     */
    quotationName?: string;
    /**
     * Date and time the Logistic Object was last submitted.
     * @type {Date}
     * @memberof QuotationResponse
     */
    submissionDateTime?: Date;
    /**
     * Start date and time of the validity period of a quotation.
     * @type {Date}
     * @memberof QuotationResponse
     */
    validityStartDateTime?: Date;
    /**
     * End date and time of the validity period of a quotation.
     * @type {Date}
     * @memberof QuotationResponse
     */
    validityEndDateTime?: Date;
    /**
     * List of contacts related to the quotation request. This can include persons or teams to be contacted about the service.
     * @type {Array<Contact>}
     * @memberof QuotationResponse
     */
    contacts?: Array<Contact>;
    /**
     * The requestor refers to the party who initiates the quotation request. In most cases, the requestor is the shipper, but in some cases, such as when dealing with complex organizations, another party may be responsible for requesting a quotation.
     * @type {Party}
     * @memberof QuotationResponse
     */
    requestor?: Party;
    /**
     * The provider is the party proposing the service.
     * @type {Party}
     * @memberof QuotationResponse
     */
    provider?: Party;
    /**
     * The invoiced party is the party invoiced for the service.
     * @type {Party}
     * @memberof QuotationResponse
     */
    invoicedParty?: Party;
    /**
     * The start location where the goods are shipped from.
     * @type {PlannedScheduledLocation}
     * @memberof QuotationResponse
     */
    startLocation?: PlannedScheduledLocation;
    /**
     * The end location where the goods are delivered.
     * @type {PlannedScheduledLocation}
     * @memberof QuotationResponse
     */
    endLocation?: PlannedScheduledLocation;
    /**
     * Selected service offer which lead to the quotation.
     * @type {QuotationRequestServiceOffer}
     * @memberof QuotationResponse
     */
    serviceOffer?: QuotationRequestServiceOffer;
    /**
     * 
     * @type {Link}
     * @memberof QuotationResponse
     */
    quotationRequest?: Link;
    /**
     * Indicates if the quotation is coming from a self care process or a CRM process. Possible values: SELF_CARE or CRM.
     * @type {string}
     * @memberof QuotationResponse
     */
    quotationProcess?: string;
    /**
     * Amount declared for insurance or customs declaration purpose.
     * @type {Value}
     * @memberof QuotationResponse
     */
    declaredValue?: Value;
    /**
     * Description of the goods being handled during the service. For a quotation, only the description of the goods is required.
     * @type {Array<QuotationBatchOfGoods>}
     * @memberof QuotationResponse
     */
    batchesOfGoods?: Array<QuotationBatchOfGoods>;
    /**
     * Sale Operations selected in the quotation to be considered in the executed service.
     * @type {Array<Operation>}
     * @memberof QuotationResponse
     */
    saleOperations?: Array<Operation>;
    /**
     * Incoterm requested by the customer. The Intercom (International Commercial Terms) is a 3 characters code defining the contractual terms between the seller and the buyer regarding the delivery of the goods and the payment of taxes. The International Chamber of Commerce (ICC) provides the list of these codes. This incoterm is the one given buy the seller to the service provider to apply the requested transport rule.
     * @type {string}
     * @memberof QuotationResponse
     */
    incoterm?: string;
    /**
     * Status of the quotation depending on the execution of the quotation process.
     * List of possible values:
     * - DRAFT: quotation was saved before it was submitted.
     * - VALID: quotation was submitted to control and can be executed by the provider.
     * - NOT_VALID: quotation was submitted to control but cannot be executed by the provider.
     * - SERVICE_REQUESTED: a service has been requested for the quotation.
     * - SERVICE_ACCEPTED: a service has been accepted for the quotation.
     * 
     * @type {string}
     * @memberof QuotationResponse
     */
    quotationStatus?: string;
    /**
     * Prepayment amount requested for the service to be executed. Sum of the prepaid amounts of the selected sale operations.
     * @type {Value}
     * @memberof QuotationResponse
     */
    requestedPrepaidAmount?: Value;
    /**
     * Total prepaid Amount for the service in the currency defined in the Value.
     * @type {Value}
     * @memberof QuotationResponse
     */
    prepaidAmount?: Value;
    /**
     * Special instructions applied to the service.
     * @type {Array<SpecialInstruction>}
     * @memberof QuotationResponse
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * Special characteristics describing the goods or the service. For example, if the requested service has to be considered as dangerous, it should appear as a special characteristics.
     * @type {Array<SpecialCharacteristic>}
     * @memberof QuotationResponse
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * List of documents used for the quotation, such as contractual documents or sales instructions.
     * @type {Array<Document>}
     * @memberof QuotationResponse
     */
    documents?: Array<Document>;
    /**
     * External references of the quotation can be used when the quotation is defined in a legacy system that registers the quotation under its own reference identifier. This can be useful for searching the Service using the ID from the legacy app. Frequent ExternalReference Types: PROVIDER_ID.
     * @type {Array<ExternalReference>}
     * @memberof QuotationResponse
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * Calculated carbon footprint.
     * @type {Value}
     * @memberof QuotationResponse
     */
    carbonFootprint?: Value;
    /**
     * Party who issued the quotation.
     * @type {Party}
     * @memberof QuotationResponse
     */
    issuer?: Party;
    /**
     * Rejections are reasons explaining why the quotation is not valid for execution.
     * @type {Array<Rejection>}
     * @memberof QuotationResponse
     */
    rejections?: Array<Rejection>;
    /**
     * DEPRECATED: Use FinancialSummary.totalAmountDue instead. Total amount due for the requested Quotation to be paid by the requestor.
     * @type {Value}
     * @memberof QuotationResponse
     */
    totalAmountDue?: Value;
    /**
     * Summary of the financial aspects of the quotation.
     * @type {FinancialSummary}
     * @memberof QuotationResponse
     */
    financialSummary?: FinancialSummary;
    /**
     * Status of the payment initiated from this quotation.
     * List of possible values:
     * - REQUESTED: The customer is ready to pay, the LoB can validate & initiate the payment.      |
     * - REFUSED: The payment has been refused by the LoB, the transaction will not be initiated. |
     * - INITIATED: A payment has been initiated for the quotation
     * - ERROR: An error happened while executing the payment.
     * - SUCCESS: The payment of the quotation has been successfully executed.
     * @type {string}
     * @memberof QuotationResponse
     */
    paymentStatus?: string;
    /**
     * Internal unique identifier of the resource. Id can have different formats like UUID v4, UUID v7, or any other string calculated.
     * @type {string}
     * @memberof QuotationResponse
     */
    id?: string;
}

/**
 * Check if a given object implements the QuotationResponse interface.
 */
export function instanceOfQuotationResponse(value: object): value is QuotationResponse {
    return true;
}

export function QuotationResponseFromJSON(json: any): QuotationResponse {
    return QuotationResponseFromJSONTyped(json, false);
}

export function QuotationResponseFromJSONTyped(json: any, ignoreDiscriminator: boolean): QuotationResponse {
    if (json == null) {
        return json;
    }
    return {
        
        'quotationName': json['quotationName'] == null ? undefined : json['quotationName'],
        'submissionDateTime': json['submissionDateTime'] == null ? undefined : (new Date(json['submissionDateTime'])),
        'validityStartDateTime': json['validityStartDateTime'] == null ? undefined : (new Date(json['validityStartDateTime'])),
        'validityEndDateTime': json['validityEndDateTime'] == null ? undefined : (new Date(json['validityEndDateTime'])),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'requestor': json['requestor'] == null ? undefined : PartyFromJSON(json['requestor']),
        'provider': json['provider'] == null ? undefined : PartyFromJSON(json['provider']),
        'invoicedParty': json['invoicedParty'] == null ? undefined : PartyFromJSON(json['invoicedParty']),
        'startLocation': json['startLocation'] == null ? undefined : PlannedScheduledLocationFromJSON(json['startLocation']),
        'endLocation': json['endLocation'] == null ? undefined : PlannedScheduledLocationFromJSON(json['endLocation']),
        'serviceOffer': json['serviceOffer'] == null ? undefined : QuotationRequestServiceOfferFromJSON(json['serviceOffer']),
        'quotationRequest': json['quotationRequest'] == null ? undefined : LinkFromJSON(json['quotationRequest']),
        'quotationProcess': json['quotationProcess'] == null ? undefined : json['quotationProcess'],
        'declaredValue': json['declaredValue'] == null ? undefined : ValueFromJSON(json['declaredValue']),
        'batchesOfGoods': json['batchesOfGoods'] == null ? undefined : ((json['batchesOfGoods'] as Array<any>).map(QuotationBatchOfGoodsFromJSON)),
        'saleOperations': json['saleOperations'] == null ? undefined : ((json['saleOperations'] as Array<any>).map(OperationFromJSON)),
        'incoterm': json['incoterm'] == null ? undefined : json['incoterm'],
        'quotationStatus': json['quotationStatus'] == null ? undefined : json['quotationStatus'],
        'requestedPrepaidAmount': json['requestedPrepaidAmount'] == null ? undefined : ValueFromJSON(json['requestedPrepaidAmount']),
        'prepaidAmount': json['prepaidAmount'] == null ? undefined : ValueFromJSON(json['prepaidAmount']),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'carbonFootprint': json['carbonFootprint'] == null ? undefined : ValueFromJSON(json['carbonFootprint']),
        'issuer': json['issuer'] == null ? undefined : PartyFromJSON(json['issuer']),
        'rejections': json['rejections'] == null ? undefined : ((json['rejections'] as Array<any>).map(RejectionFromJSON)),
        'totalAmountDue': json['totalAmountDue'] == null ? undefined : ValueFromJSON(json['totalAmountDue']),
        'financialSummary': json['financialSummary'] == null ? undefined : FinancialSummaryFromJSON(json['financialSummary']),
        'paymentStatus': json['paymentStatus'] == null ? undefined : json['paymentStatus'],
        'id': json['id'] == null ? undefined : json['id'],
    };
}

export function QuotationResponseToJSON(json: any): QuotationResponse {
    return QuotationResponseToJSONTyped(json, false);
}

export function QuotationResponseToJSONTyped(value?: QuotationResponse | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'quotationName': value['quotationName'],
        'submissionDateTime': value['submissionDateTime'] == null ? value['submissionDateTime'] : value['submissionDateTime'].toISOString(),
        'validityStartDateTime': value['validityStartDateTime'] == null ? value['validityStartDateTime'] : value['validityStartDateTime'].toISOString(),
        'validityEndDateTime': value['validityEndDateTime'] == null ? value['validityEndDateTime'] : value['validityEndDateTime'].toISOString(),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'requestor': PartyToJSON(value['requestor']),
        'provider': PartyToJSON(value['provider']),
        'invoicedParty': PartyToJSON(value['invoicedParty']),
        'startLocation': PlannedScheduledLocationToJSON(value['startLocation']),
        'endLocation': PlannedScheduledLocationToJSON(value['endLocation']),
        'serviceOffer': QuotationRequestServiceOfferToJSON(value['serviceOffer']),
        'quotationRequest': LinkToJSON(value['quotationRequest']),
        'quotationProcess': value['quotationProcess'],
        'declaredValue': ValueToJSON(value['declaredValue']),
        'batchesOfGoods': value['batchesOfGoods'] == null ? undefined : ((value['batchesOfGoods'] as Array<any>).map(QuotationBatchOfGoodsToJSON)),
        'saleOperations': value['saleOperations'] == null ? undefined : ((value['saleOperations'] as Array<any>).map(OperationToJSON)),
        'incoterm': value['incoterm'],
        'quotationStatus': value['quotationStatus'],
        'requestedPrepaidAmount': ValueToJSON(value['requestedPrepaidAmount']),
        'prepaidAmount': ValueToJSON(value['prepaidAmount']),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'carbonFootprint': ValueToJSON(value['carbonFootprint']),
        'issuer': PartyToJSON(value['issuer']),
        'rejections': value['rejections'] == null ? undefined : ((value['rejections'] as Array<any>).map(RejectionToJSON)),
        'totalAmountDue': ValueToJSON(value['totalAmountDue']),
        'financialSummary': FinancialSummaryToJSON(value['financialSummary']),
        'paymentStatus': value['paymentStatus'],
        'id': value['id'],
    };
}

